/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from 'react-router';
import { startOfTomorrow } from 'date-fns';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { paths } from 'src/routes/paths';

import { useRoute } from 'src/hooks/use-route';
import useHandleError from 'src/hooks/use-handle-error';
import { useResponsive } from 'src/hooks/use-responsive';

import { moveRouterLink } from 'src/utils/moveLinkUtil';
import { momentTimestamp } from 'src/utils/moment-format';
import { storageConstants } from 'src/utils/constants/storageConstants';

import popupApi from 'src/api/popupApi';

import Image from '../image';

interface PopupModalProps {
  open: boolean;
  onClose: VoidFunction;
}

export default function PopupModal({ open, onClose }: Readonly<PopupModalProps>) {
  const [popupData, setPopupData] = useState<any>(null);
  const route = useRoute();
  const [searchParams] = useSearchParams();

  const handleError = useHandleError();
  const navigate = useNavigate();
  const isMobile = useResponsive('down', 'md');
  const { t } = useTranslation();

  useEffect(() => {
    localStorage.removeItem(storageConstants.showPopupDate);
    fetchData();
  }, []);

  const fetchData = handleError(async () => {
    try {
      const response = await popupApi.fetchpopupDetail();
      setPopupData(response.data.data);
    } catch (err) {
      route.goBack();
    }
  });

  const handleClose = () => {
    const autoLogin = searchParams.get('autoLogin');

    route.pushNavigate(paths.home, autoLogin ? 'popupCheck=true' : '');

    // Set checked popup to true when user clse the popup content modal
    localStorage.setItem(storageConstants.checkedPopup, 'true');
    onClose();
  };

  const showLater = () => {
    localStorage.setItem(
      storageConstants.showPopupDate,
      momentTimestamp(startOfTomorrow()).toString()
    );

    handleClose();
  };

  const goToDynamicLink = (item: any) => {
    console.log('item', item);
    moveRouterLink(navigate, item);
    onClose();
  };

  return (
    popupData && (
      <Dialog open={open} fullScreen={isMobile} onClose={handleClose} maxWidth="lg">
        <Stack
          spacing={0.5}
          direction="row"
          alignItems="center"
          p={{ xs: '0 16px', md: 3 }}
          justifyContent="space-between"
          minHeight={{ xs: 64, md: 'unset' }}
          position="sticky"
          top={0}
          zIndex={10}
          bgcolor="var(--background-default)"
        >
          <DialogTitle
            sx={{
              p: 0,
              height: { md: 48 },
              flexGrow: { xs: 1, md: 0 },
              typography: { xs: 'h4', md: 'h3' },
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography variant={isMobile ? 'h4' : 'h3'}>{t('learner.1890')}</Typography>
          </DialogTitle>
        </Stack>

        <DialogContent sx={{ p: 0 }}>
          <Stack width={1}>
            <Image
              onClick={() => goToDynamicLink(popupData[0])}
              src={popupData[0].image.resourceUri}
              sx={{ cursor: 'pointer' }}
              useIntersectionObserver={false}
              visibleByDefault
              disabledEffect
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: {
              xs: '16px 16px 36px',
              md: '24px 24px 16px',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'var(--background-default)',
              zIndex: 100,
              marginTop: 'auto',
              borderTop: '1px solid var(--components-divider)',
            },
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={showLater} variant="outlined">
              {t('learner.4402')}
            </Button>
            <Button onClick={handleClose} variant="contained">
              {t('learner.4403')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    )
  );
}
